import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  FolderState,
  FolderListParams,
  FolderListResponse,
  FolderActionResponse,
  CreateFolderRequest,
  UpdateFolderRequest,
  Folder,
} from '@/constants/types/folder.types';
import { folderService } from '@/services/admin/folderService';

// Initial State
const initialState: FolderState = {
  folders: [],
  pagination: null,
  isLoading: false,
  isActionLoading: false,
  error: null,
  currentFolder: null,
};

// Async Thunks
export const fetchFolders = createAsyncThunk(
  'folders/fetchFolders',
  async (params: FolderListParams, { rejectWithValue }) => {
    try {
      const response = await folderService.getFolders(params);
      return response;
    } catch (error: any) {
      console.error('Error fetching folders:', error);
      const errorMessage =
        error?.message || 'Failed to fetch folders';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createFolder = createAsyncThunk(
  'folders/createFolder',
  async (data: CreateFolderRequest, { rejectWithValue }) => {
    try {
      const response = await folderService.createFolder(data);
      return response;
    } catch (error: any) {
      console.error('Error creating folder:', error);
      const errorMessage =
        error?.message || 'Failed to create folder';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateFolder = createAsyncThunk(
  'folders/updateFolder',
  async (
    { id, data }: { id: string; data: UpdateFolderRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await folderService.updateFolder(id, data);
      return { id, ...response };
    } catch (error: any) {
      console.error('Error updating folder:', error);
      const errorMessage =
        error?.message || 'Failed to update folder';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'folders/deleteFolder',
  async (id: string, { rejectWithValue }) => {
    try {
      await folderService.deleteFolder(id);
      return id;
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      const errorMessage =
        error?.message || 'Failed to delete folder';
      return rejectWithValue(errorMessage);
    }
  }
);

// Slice
const folderSlice = createSlice({
  name: 'folders',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetFolderState: () => initialState,
    setCurrentFolder: (state, action: PayloadAction<Folder | null>) => {
      state.currentFolder = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Folders
      .addCase(fetchFolders.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(
        fetchFolders.fulfilled,
        (state, action: PayloadAction<FolderListResponse>) => {
          state.isLoading = false;
          state.folders = action.payload.data.folders;
          state.pagination = action.payload.data.pagination;
          state.error = null;
        }
      )
      .addCase(fetchFolders.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Create Folder
      .addCase(createFolder.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(
        createFolder.fulfilled,
        (state, action: PayloadAction<FolderActionResponse>) => {
          state.isActionLoading = false;
          if (action.payload.data?.folder) {
            // Optimistically add to the list (will be replaced on next fetch)
            state.folders.unshift(action.payload.data.folder);
          }
          state.error = null;
        }
      )
      .addCase(createFolder.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })

      // Update Folder
      .addCase(updateFolder.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(updateFolder.fulfilled, (state, action: PayloadAction<any>) => {
        state.isActionLoading = false;
        const { id, data } = action.payload;
        const folderIndex = state.folders.findIndex((f) => f._id === id);
        if (folderIndex !== -1 && data?.folder) {
          state.folders[folderIndex] = data.folder;
        }
        state.currentFolder = null;
        state.error = null;
      })
      .addCase(updateFolder.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      })

      // Delete Folder
      .addCase(deleteFolder.pending, (state) => {
        state.isActionLoading = true;
        state.error = null;
      })
      .addCase(deleteFolder.fulfilled, (state, action: PayloadAction<string>) => {
        state.isActionLoading = false;
        state.folders = state.folders.filter((f) => f._id !== action.payload);
        if (state.pagination) {
          state.pagination.total -= 1;
        }
        state.error = null;
      })
      .addCase(deleteFolder.rejected, (state, action) => {
        state.isActionLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetFolderState, setCurrentFolder } =
  folderSlice.actions;
export default folderSlice.reducer;
